import { useState, useEffect, useCallback } from 'react'
import useRouter from 'utils/useRouter'
import { useCadaverService } from 'hooks/useCadaverService'

export const useFormReconhecimento = (props) => {
  const { cadaver } = props
  const router = useRouter()
  const cadaverService = useCadaverService()

  const [activeStep, setActiveStep] = useState(0)
  const [processando, setProcessando] = useState(false)
  const [openEspelho, setOpenEspelho] = useState(false)
  const [dadosFormatados, setDadosFormatados] = useState({})

  // FormState do Cadáver
  const [cadaverFormState, setCadaverFormState] = useState({
    values: {
      nome: '',
      naturalidade: '',
      dataNascimento: null,
      filiacao0: '',
      filiacao1: '',
      sexo: '',
    },
    errors: {},
    touched: {},
  })

  // FormState do Reconhecedor
  const [pessoaFormState, setPessoaFormState] = useState({
    values: {
      nome: '',
      tipoDocumento: '',
      numeroDocumento: '',
      orgaoEmissorDocumento: '',
      telefone: '',
      grauRelacao: '',
      arquivos: [],
    },
    errors: {},
    touched: {},
  })

  // Sincroniza dados iniciais do cadáver quando carregado da API
  useEffect(() => {
    if (cadaver && cadaver.id) {
      setCadaverFormState((prev) => ({
        ...prev,
        values: {
          ...prev.values,
          nome: cadaver.nome || '',
          sexo: cadaver.sexo || 'DESCONHECIDO',
          naturalidade: cadaver.naturalidade || '',
          dataNascimento: cadaver.dataNascimento || null,
        },
      }))
    }
  }, [cadaver])

  // Validação do Cadáver (Etapa 0)
  useEffect(() => {
    const errors = {}
    if (!cadaverFormState.values.nome || !cadaverFormState.values.nome.trim()) {
      errors.nome = ['Nome é obrigatório']
    }
    if (!cadaverFormState.values.filiacao1 || !cadaverFormState.values.filiacao1.trim()) {
      errors.filiacao1 = ['Filiação é obrigatória']
    }
    setCadaverFormState((prev) => ({ ...prev, errors }))
  }, [cadaverFormState.values.nome, cadaverFormState.values.filiacao1])

  // Validação do Reconhecedor (Etapa 1)
  useEffect(() => {
    const errors = {}
    if (!pessoaFormState.values.nome || !pessoaFormState.values.nome.trim()) {
      errors.nome = ['Nome é obrigatório']
    }
    if (!pessoaFormState.values.tipoDocumento) {
      errors.tipoDocumento = ['Tipo do documento é obrigatório']
    }
    if (!pessoaFormState.values.numeroDocumento || !pessoaFormState.values.numeroDocumento.trim()) {
      errors.numeroDocumento = ['Número do documento é obrigatório']
    }
    if (!pessoaFormState.values.orgaoEmissorDocumento || !pessoaFormState.values.orgaoEmissorDocumento.trim()) {
      errors.orgaoEmissorDocumento = ['Órgão emissor é obrigatório']
    }
    if (!pessoaFormState.values.telefone || !pessoaFormState.values.telefone.trim()) {
      errors.telefone = ['Telefone é obrigatório']
    }
    if (!pessoaFormState.values.grauRelacao) {
      errors.grauRelacao = ['Grau de relação é obrigatório']
    }
    setPessoaFormState((prev) => ({ ...prev, errors }))
  }, [
    pessoaFormState.values.nome,
    pessoaFormState.values.tipoDocumento,
    pessoaFormState.values.numeroDocumento,
    pessoaFormState.values.orgaoEmissorDocumento,
    pessoaFormState.values.telefone,
    pessoaFormState.values.grauRelacao,
  ])

  // Emulador de FormState para o componente FormCadaver
  const cadaverForm = {
    formState: cadaverFormState,
    setFormState: setCadaverFormState,
    handleChange: useCallback((event) => {
      const { name, value } = event.target
      setCadaverFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: value },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    handleChangeNewValueByNameProperty: useCallback((event, newValue, name) => {
      setCadaverFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: newValue },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    handleChangeCampoData: useCallback((name, date) => {
      setCadaverFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: date },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    hasError: useCallback((name) => Boolean(cadaverFormState.errors[name]), [cadaverFormState.errors]),
  }

  // Emulador de FormState para o componente FormPessoaReconhecimento e FormArquivos
  const pessoaForm = {
    formState: pessoaFormState,
    setFormState: setPessoaFormState,
    handleChange: useCallback((event) => {
      const { name, value } = event.target
      setPessoaFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: value },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    handleChangeNewValueByNameProperty: useCallback((event, newValue, name) => {
      setPessoaFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: newValue },
        touched: { ...prev.touched, [name]: true },
      }))
    }, []),
    hasError: useCallback((name) => Boolean(pessoaFormState.errors[name]), [pessoaFormState.errors]),
  }

  const formError = () => {
    if (activeStep === 0) {
      return Boolean(cadaverFormState.errors.nome || cadaverFormState.errors.filiacao1)
    }
    if (activeStep === 1) {
      return Boolean(
        pessoaFormState.errors.nome ||
        pessoaFormState.errors.tipoDocumento ||
        pessoaFormState.errors.numeroDocumento ||
        pessoaFormState.errors.orgaoEmissorDocumento ||
        pessoaFormState.errors.telefone ||
        pessoaFormState.errors.grauRelacao
      )
    }
    return false
  }

  const handleBack = () => {
    setActiveStep((prev) => prev - 1)
  }

  const handleNext = () => {
    if (activeStep === 2) {
      // Finalizar Reconhecimento e estruturar payload
      const filiacoes = []
      Object.keys(cadaverFormState.values).forEach((key) => {
        if (key.indexOf('filiacao') !== -1 && cadaverFormState.values[key]) {
          filiacoes.push(cadaverFormState.values[key])
        }
      })

      const dados = {
        cadaver: {
          id: cadaver.id,
          filiacoes,
          ...cadaverFormState.values,
        },
        reconhecedor: {
          ...pessoaFormState.values,
        },
      }
      setDadosFormatados('dados')
      setDadosFormatados(dados)
      setOpenEspelho(true)
    } else {
      setActiveStep((prev) => prev + 1)
    }
  }

  const handleConfirmar = async () => {
    await cadaverService.reconhecerCadaver(dadosFormatados, setProcessando)
    router.history.push(`/casos/caso/cadaver/detalhar/${cadaver.id}`)
  }

  return {
    activeStep,
    steps: ['Cadáver', 'Reconhecedor', 'Arquivos Relacionados'],
    cadaverForm,
    pessoaForm,
    processando,
    openEspelho,
    setOpenEspelho,
    dadosFormatados,
    formError,
    handleBack,
    handleNext,
    handleConfirmar,
    router,
  }
}
